import { MIN_PASSWORD_LENGTH, ROLE_LABELS, changePasswordSchema } from '@pietrochip/shared';
import { useState } from 'react';
import * as api from '../api/endpoints';
import { describeError } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useToasts } from '../components/Toasts';
import { Field, Notice } from '../components/ui';

/**
 * Il proprio account: chi sei, come cambiare password, e come uscire.
 *
 * Non c'è altro da mettere, e non è una mancanza: nome, email e ruolo li
 * decidono gli amministratori, perché cambiano cosa puoi fare sull'evento.
 *
 * L'uscita sta qui perché sul telefono la barra non ha spazio per «Esci»
 * accanto al nome: senza questo pulsante, da un telefono non si uscirebbe più.
 */
export function AccountPage() {
  const { user, signOut, freeAccess } = useAuth();
  const { push } = useToasts();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  async function onSubmit() {
    if (newPassword !== repeat) {
      setError('Le due password non coincidono.');
      return;
    }

    const parsed = changePasswordSchema.safeParse({ currentPassword, newPassword });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Controlla i dati inseriti.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      await api.changeOwnPassword(parsed.data);
      setCurrentPassword('');
      setNewPassword('');
      setRepeat('');
      push({ tone: 'neutral', message: 'Password cambiata. Le altre sessioni sono state chiuse.' });
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Il tuo account</h1>
          <p className="page-head__meta">
            {user.name} · {ROLE_LABELS[user.role]}
          </p>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card__body stack">
            <h2 className="card__title">Cambia password</h2>

            <form
              className="stack"
              onSubmit={(event) => {
                event.preventDefault();
                void onSubmit();
              }}
            >
              <Field label="Password attuale">
                {({ id, ...aria }) => (
                  <input
                    {...aria}
                    id={id}
                    className="input"
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    required
                  />
                )}
              </Field>

              <Field label="Nuova password" hint={`Almeno ${MIN_PASSWORD_LENGTH} caratteri.`}>
                {({ id, ...aria }) => (
                  <input
                    {...aria}
                    id={id}
                    className="input"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    minLength={MIN_PASSWORD_LENGTH}
                    required
                  />
                )}
              </Field>

              <Field label="Ripeti la nuova password">
                {({ id, ...aria }) => (
                  <input
                    {...aria}
                    id={id}
                    className="input"
                    type="password"
                    autoComplete="new-password"
                    value={repeat}
                    onChange={(event) => setRepeat(event.target.value)}
                    minLength={MIN_PASSWORD_LENGTH}
                    required
                  />
                )}
              </Field>

              {error && <Notice tone="danger">{error}</Notice>}

              <button
                type="submit"
                className="btn btn--primary btn--lg"
                disabled={busy || currentPassword === '' || newPassword === ''}
              >
                {busy ? 'Salvataggio…' : 'Cambia password'}
              </button>
            </form>
          </div>
        </div>

        <aside className="card">
          <div className="card__body stack">
            <h2 className="card__title">Dati dell’account</h2>
            <div className="stack" style={{ gap: 10, fontSize: '0.9375rem' }}>
              <div>
                <div className="label">Nome</div>
                <div>{user.name}</div>
              </div>
              <div>
                <div className="label">Email</div>
                <div className="mono">{user.email}</div>
              </div>
              <div>
                <div className="label">Ruolo</div>
                <div>{ROLE_LABELS[user.role]}</div>
              </div>
            </div>
            <p className="faint" style={{ fontSize: '0.8125rem' }}>
              Nome, email e ruolo si cambiano dall’area Utenti, da un amministratore.
            </p>

            {/*
              Con l'accesso libero non si mostra: la richiesta successiva
              rientrerebbe da sola con l'utente di riserva, ed è lo stesso
              motivo per cui la barra non lo mostra già.
            */}
            {!freeAccess && (
              <button type="button" className="btn btn--lg" onClick={() => void signOut()}>
                Esci dall’account
              </button>
            )}
          </div>
        </aside>
      </div>
    </>
  );
}
